/**
 * AHRI / Paired With list: role filters, width rules, outdoor search,
 * SEER2 filter/sort, View System, and pagination.
 *
 * Width rule: a coil/furnace pair is compatible only when both widths
 * parse as numbers and those numbers are equal. 21.0, 21", and 21 in
 * all equal 21. 10.0 is 10. 18.5 and 21.25 stay as written.
 * Missing width => not compatible when a width check is required.
 */
(function () {
  // onerror only covers a failed fetch. An evaluation error or a failed
  // customElements.define never reaches it, and must restore before rethrow.
  try {
  const AhriListUtils = {
    parseSeer(value) {
      const cleaned = String(value || '')
        .replace(/seer2?/gi, '')
        .replace(/[^\d.]+/g, ' ')
        .trim();
      const n = parseFloat(cleaned);
      return Number.isFinite(n) ? n : null;
    },
    matchesSearch(hay, query) {
      const needle = String(query || '').toLowerCase().trim();
      if (!needle) return true;
      return String(hay || '').toLowerCase().includes(needle);
    },
    pageCount(total, pageSize) {
      const size = Math.max(1, Number(pageSize) || 5);
      return Math.max(1, Math.ceil(Math.max(0, total) / size));
    },
    pageSlice(items, page, pageSize) {
      const size = Math.max(1, Number(pageSize) || 5);
      const current = Math.max(1, Number(page) || 1);
      const start = (current - 1) * size;
      return items.slice(start, start + size);
    },
    listRequestToken(handle, page, query, filter, seer, sort, coil, pageSize, pdpRole) {
      const cleanQuery = String(query || '')
        .replace(/\|\|\|/g, ' ')
        .trim();
      const number = Number(page);
      const size = Math.max(1, Math.min(5, Number(pageSize) || 5));
      return [
        String(handle || '').trim(),
        String(!number || number < 1 ? 1 : number),
        cleanQuery,
        String(filter || '').trim(),
        String(seer || '').trim(),
        String(sort || '').trim(),
        String(coil || '').trim(),
        String(size),
        String(pdpRole || '').trim(),
      ].join('|||');
    },
    visiblePageNumbers(current, total) {
      const pages = [];
      const max = Math.max(1, Number(total) || 1);
      const cur = Math.min(max, Math.max(1, Number(current) || 1));
      const windowSize = 1;
      const start = Math.max(1, cur - windowSize);
      const end = Math.min(max, cur + windowSize);
      if (start > 1) pages.push(1);
      if (start > 2) pages.push('…');
      for (let i = start; i <= end; i += 1) pages.push(i);
      if (end < max - 1) pages.push('…');
      if (end < max) pages.push(max);
      return pages;
    },
    compareSeer(a, b, dir) {
      const sa = AhriListUtils.parseSeer(a);
      const sb = AhriListUtils.parseSeer(b);
      if (sa == null && sb == null) return 0;
      if (sa == null) return 1;
      if (sb == null) return -1;
      return dir === 'asc' ? sa - sb : sb - sa;
    },
    formatCents(cents) {
      const n = Number(cents);
      if (!Number.isFinite(n) || n <= 0) return '';
      return `$${(n / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    // Adam's locked rule (same as AhriPackageUtils): kit BTU = kW × 3412;
    // recommend only when that is 70–110% of outdoor/AH size-code BTU.
    HEAT_KIT_KW_TO_BTU: 3412,
    HEAT_KIT_RECOMMEND_MIN_RATIO: 0.7,
    HEAT_KIT_RECOMMEND_MAX_RATIO: 1.1,
    CAPACITY_TO_BTU: {
      '018': 18000,
      '024': 24000,
      '030': 30000,
      '036': 36000,
      '042': 42000,
      '048': 48000,
      '060': 60000,
      18: 18000,
      24: 24000,
      30: 30000,
      36: 36000,
      42: 42000,
      48: 48000,
      60: 60000,
    },
    parseKitKw(text) {
      const hay = String(text || '');
      const labeled = hay.match(/(\d+(?:\.\d+)?)\s*kW/i);
      if (labeled) return labeled[1];
      const model = hay.toUpperCase().match(/ECBA?\d{2}[-_](\d{1,2}(?:\.\d+)?)/);
      return model ? model[1] : '';
    },
    parseSizeCodeBtu(text) {
      const raw = String(text || '');
      const hay = raw.toUpperCase();
      const indoor = hay.match(
        /(?:^|[^A-Z0-9])(?:CBA|CBX|CBK|CBC|MB)[A-Z0-9]*[- _]*?(018|024|030|036|042|048|060)(?:[-_ ]|$)/
      );
      if (indoor) return AhriListUtils.CAPACITY_TO_BTU[indoor[1]] || 0;
      const outdoor = hay.match(/[-_](018|024|030|036|042|048|060|18|24|30|36|42|48|60)(?:[-_ ]|[A-Z]|$)/);
      if (outdoor) return AhriListUtils.CAPACITY_TO_BTU[outdoor[1]] || 0;
      const labeled = raw.replace(/,/g, '').match(/(\d{4,6})\s*BTU/i);
      if (labeled) {
        const n = Number(labeled[1]);
        const allowed = Object.keys(AhriListUtils.CAPACITY_TO_BTU).map(
          (key) => AhriListUtils.CAPACITY_TO_BTU[key]
        );
        if (allowed.indexOf(n) >= 0) return n;
      }
      return 0;
    },
    partnerSizeHay(part) {
      if (!part) return '';
      return [part.title, part.handle, part.sku, part.model].filter(Boolean).join(' ');
    },
    systemBtuFromSizeCode(outdoor, airHandler) {
      const pkg = typeof window !== 'undefined' ? window.AhriPackageUtils : null;
      if (pkg && pkg.systemBtuFromSizeCode) return pkg.systemBtuFromSizeCode(outdoor, airHandler);
      return (
        AhriListUtils.parseSizeCodeBtu(AhriListUtils.partnerSizeHay(outdoor)) ||
        AhriListUtils.parseSizeCodeBtu(AhriListUtils.partnerSizeHay(airHandler)) ||
        0
      );
    },
    // AH PDP: size from this air handler. Outdoor PDP: size from this outdoor.
    // Never use a random outdoor from the matchup list (those can be 024–060).
    pdpSystemBtu(dataset) {
      const data = dataset || {};
      const pdp = { title: data.pdpTitle || '', handle: data.pdpHandle || '' };
      if (data.pdpRole === 'outdoor') return AhriListUtils.systemBtuFromSizeCode(pdp, null);
      return AhriListUtils.systemBtuFromSizeCode(null, pdp);
    },
    heatKitOutputBtu(kit) {
      const pkg = typeof window !== 'undefined' ? window.AhriPackageUtils : null;
      if (pkg && pkg.heatKitOutputBtu) return pkg.heatKitOutputBtu(kit);
      const kw = Number((kit && kit.kw) || AhriListUtils.parseKitKw((kit && (kit.title || kit.handle || kit.sku)) || ''));
      if (!kw) return 0;
      return kw * AhriListUtils.HEAT_KIT_KW_TO_BTU;
    },
    heatKitIsRecommended(kit, systemBtu) {
      const pkg = typeof window !== 'undefined' ? window.AhriPackageUtils : null;
      if (pkg && pkg.heatKitIsRecommended) return pkg.heatKitIsRecommended(kit, systemBtu);
      const kitBtu = AhriListUtils.heatKitOutputBtu(kit);
      const system = Number(systemBtu) || 0;
      if (!kitBtu || !system) return false;
      const ratio = kitBtu / system;
      return (
        ratio >= AhriListUtils.HEAT_KIT_RECOMMEND_MIN_RATIO &&
        ratio <= AhriListUtils.HEAT_KIT_RECOMMEND_MAX_RATIO
      );
    },
    parseIndoorSeries(text) {
      const hay = String(text || '').toUpperCase();
      const indoor = hay.match(
        /(?:^|[^A-Z0-9])((?:ECBA|ECB|CBK|CBA|CBX)\d{2}|CBC\d|MB[A-Z]{2,3})/
      );
      return indoor ? indoor[1] : '';
    },
    heatKitSeriesKeys(ahSeries) {
      const pkg = typeof window !== 'undefined' ? window.AhriPackageUtils : null;
      if (pkg && pkg.heatKitSeriesKeys) return pkg.heatKitSeriesKeys(ahSeries);
      const series = String(ahSeries || '').toUpperCase();
      const keys = [];
      const push = (value) => {
        if (value && keys.indexOf(value) < 0) keys.push(value);
      };
      if (!series) return keys;
      if (/^CB[AKX]\d{2}$/.test(series) || /^CBC\d$/.test(series)) push(`E${series}`);
      const digits = (series.match(/(\d+)/) || [])[1];
      if (digits) push(`ECB${digits}`);
      push(series);
      return keys;
    },
    heatKitSeriesRank(kit, airHandler) {
      const pkg = typeof window !== 'undefined' ? window.AhriPackageUtils : null;
      if (pkg && pkg.heatKitSeriesRank) return pkg.heatKitSeriesRank(kit, airHandler);
      const ahSeries = AhriListUtils.parseIndoorSeries(
        (airHandler && (airHandler.series || airHandler.title || airHandler.handle)) || ''
      );
      const keys = AhriListUtils.heatKitSeriesKeys(ahSeries);
      if (!keys.length) return 99;
      const kitHay = String((kit && (kit.title || kit.handle || kit.sku || kit.series)) || '').toUpperCase();
      const kitSeries = AhriListUtils.parseIndoorSeries(kitHay);
      const idx = keys.findIndex((key) => kitSeries === key || kitHay.indexOf(key) >= 0);
      return idx < 0 ? 99 : idx;
    },
    kitFromHeatKitCard(card) {
      if (!card) return { title: '', handle: '' };
      const title = card.querySelector('.ahri-partner__title');
      const sku = card.querySelector('.ahri-partner__sku');
      return {
        title: title ? title.textContent.trim() : '',
        handle: card.dataset.partnerKey || '',
        sku: sku ? sku.textContent.trim() : '',
      };
    },
    sortHeatKitCards(cards, systemBtu, airHandler) {
      return (cards || []).slice().sort((a, b) => {
        const kitA = a.kit || AhriListUtils.kitFromHeatKitCard(a.card || a);
        const kitB = b.kit || AhriListUtils.kitFromHeatKitCard(b.card || b);
        const recA = AhriListUtils.heatKitIsRecommended(kitA, systemBtu) ? 0 : 1;
        const recB = AhriListUtils.heatKitIsRecommended(kitB, systemBtu) ? 0 : 1;
        if (recA !== recB) return recA - recB;
        const series =
          AhriListUtils.heatKitSeriesRank(kitA, airHandler) - AhriListUtils.heatKitSeriesRank(kitB, airHandler);
        if (series) return series;
        const kwA = Number(kitA.kw || AhriListUtils.parseKitKw(kitA.title || kitA.handle || kitA.sku) || 0);
        const kwB = Number(kitB.kw || AhriListUtils.parseKitKw(kitB.title || kitB.handle || kitB.sku) || 0);
        if (kwA !== kwB) return kwA - kwB;
        return String(kitA.title || kitA.handle || '').localeCompare(String(kitB.title || kitB.handle || ''));
      });
    },
    parseCabinetWidth(value) {
      const raw = String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/inches/g, '')
        .replace(/inch/g, '')
        .replace(/in/g, '')
        .replace(/["″”']/g, '')
        .replace(/[\s-]/g, '');
      if (!/^\d+(?:\.\d+)?$/.test(raw)) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    // Same letter rule as snippets/ahri-coil-cabinet-match.liquid.
    coilCabinetLetter(text) {
      const upper = String(text || '')
        .toUpperCase()
        .split('+')[0]
        .trim();
      let letter = '';
      for (const part of upper.split('-')) {
        let token = part.trim();
        if (token.includes('/')) token = token.split('/').pop().trim();
        const d0 = token.slice(0, 1);
        const d1 = token.slice(1, 2);
        const d2 = token.slice(2, 3);
        const d3 = token.slice(3, 4);
        if (d0 && d1 && '0123456789'.includes(d0) && '0123456789'.includes(d1)) {
          if (d2 === 'A' || d2 === 'B' || d2 === 'C' || d2 === 'D') {
            if (d3 === '' || d3 === '*') letter = d2;
          }
        }
      }
      return letter;
    },
    coilExactKey(text) {
      let key = String(text || '')
        .toUpperCase()
        .split('+')[0]
        .trim()
        .replace(/\*/g, '');
      if (key.endsWith('-')) key = key.slice(0, -1);
      return key;
    },
    coilCabinetOk(model, sku) {
      const ahri = AhriListUtils.coilCabinetLetter(model);
      const stock = AhriListUtils.coilCabinetLetter(sku);
      if (ahri && stock && ahri === stock) return true;
      if (!ahri && !stock) {
        const modelKey = AhriListUtils.coilExactKey(model);
        const skuKey = AhriListUtils.coilExactKey(sku);
        return Boolean(modelKey && modelKey === skuKey);
      }
      return false;
    },
    partSku(part, fallback) {
      if (part == null) return fallback || '';
      return typeof part === 'string' ? fallback || '' : part.sku || fallback || '';
    },
    partIsStocked(part, fallbackSku, catalogFallback) {
      if (part == null || part === '') return true;
      const sku = AhriListUtils.partSku(part, fallbackSku);
      const catalog = String(
        (typeof part === 'object' && part && part.in_catalog) || catalogFallback || ''
      ).toLowerCase();
      return Boolean(sku) && catalog !== 'no';
    },
    rowIsStocked(row) {
      if (!row) return false;
      const indoor = (row.indoor && row.indoor.value) || row.indoor || row.coil || {};
      const outdoor = (row.outdoor && row.outdoor.value) || row.outdoor;
      const type = String(row.indoor_type || '').toLowerCase();
      const sku = AhriListUtils.partSku(indoor, row.indoor_sku);
      const catalog = String(
        (typeof indoor === 'object' && indoor && indoor.in_catalog) || row.in_catalog || ''
      ).toLowerCase();
      const model =
        typeof indoor === 'string'
          ? indoor
          : String((indoor && (indoor.model || indoor.title || indoor.sku)) || row.indoor_model || '').trim();
      if (!AhriListUtils.partIsStocked(outdoor, row.outdoor_sku, row.outdoor_catalog)) return false;
      if (type === 'air_handler') return Boolean(sku) && catalog !== 'no';
      if (!model && !sku) return true;
      if (!sku || catalog === 'no') return false;
      return AhriListUtils.coilCabinetOk(model, sku);
    },
    widthsEqual(a, b) {
      const left = AhriListUtils.parseCabinetWidth(a);
      const right = AhriListUtils.parseCabinetWidth(b);
      if (left == null || right == null) return false;
      return left === right;
    },
    searchHay(item) {
      if (!item) return '';
      const parts = [
        item.dataset.search || '',
        item.dataset.outdoorModel || '',
        item.dataset.ahriNumber || '',
        item.dataset.seer || '',
      ];
      item.querySelectorAll('.ahri-partner__title, .ahri-partner__sku').forEach((el) => {
        parts.push(el.textContent || '');
      });
      return parts.join(' ');
    },
    detailFocusNeedsScroll(rect, marginTop, viewWidth, viewHeight) {
      if (!rect) return false;
      const top = Number.isFinite(rect.top) ? rect.top : 0;
      const bottom = Number.isFinite(rect.bottom) ? rect.bottom : 0;
      const left = Number.isFinite(rect.left) ? rect.left : 0;
      const right = Number.isFinite(rect.right) ? rect.right : 0;
      const margin = Number.isFinite(marginTop) && marginTop > 0 ? marginTop : 0;
      const height = Number.isFinite(viewHeight) && viewHeight > 0 ? viewHeight : 0;
      const width = Number.isFinite(viewWidth) && viewWidth > 0 ? viewWidth : 0;
      if (top < margin) return true;
      if (height && bottom > height) return true;
      if (left < 0) return true;
      if (width && right > width) return true;
      return false;
    },
  };

  window.AhriListUtils = AhriListUtils;

  if (!customElements.get('ahri-compatible-systems')) {
    customElements.define(
      'ahri-compatible-systems',
      class AhriCompatibleSystems extends HTMLElement {
        connectedCallback() {
          try {
            this.pdpRole = this.dataset.pdpRole || 'unknown';
            this.pdpWidth = (this.dataset.pdpWidth || '').trim();
            this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 5);
            this.page = 1;
            this.searchQuery = '';
            this.minSeer = '';
            this.seerSort = 'default';
            this.viewingItem = null;
            this.detailReturnFocus = null;
            this.listHost = this.querySelector('.ahri-compatible-systems__list');
            this.items = Array.from(this.querySelectorAll('[data-ahri-item]'));
            this.listUrl = this.dataset.ahriListUrl || '';
            this.catalogTotal = Math.max(0, Number(this.dataset.ahriListTotal) || 0);
            this.listTotal = Math.max(0, Number(this.dataset.ahriListTotal) || 0);
            this.serverRows = [];
            this.serverTotal = 0;
            this.serverPage = 1;
            this.shownPage = 1;
            this.rowsByAhri = new Map();
            this.rowsLoaded = !this.listUrl || (this.catalogTotal > 0 && this.catalogTotal <= this.items.length);
            this.pagedFromServer = false;
            this.loadedRequestKey = '';
            this.pendingRequestKey = '';
            this.failedRequestKey = '';
            this.listLoadFailed = false;
            this.listError = this.querySelector('[data-ahri-list-error]');
            this.listLoading = this.querySelector('[data-ahri-list-loading]');
            this.listRetry = this.querySelector('[data-ahri-list-retry]');
            this.inlineByAhri = new Map(
              this.items.map((item) => [item.dataset.ahriNumber, item]).filter((entry) => entry[0])
            );
            this.restByAhri = new Map();
            this.heatKits = this.querySelector('[data-ahri-heat-kits]');
            this.empty = this.querySelector('[data-ahri-empty]');
            this.coilFilters = this.querySelector('[data-ahri-coil-filters]');
            this.coilChipHost = this.coilFilters ? this.coilFilters.querySelector('[data-ahri-coil-chips]') : null;
            this.filterButtons = Array.from(this.querySelectorAll('[data-ahri-filter]'));
            this.searchInput = this.querySelector('[data-ahri-search]');
            this.seerMinSelect = this.querySelector('[data-ahri-seer-min]');
            this.seerSortSelect = this.querySelector('[data-ahri-seer-sort]');
            this.pagination = this.querySelector('[data-ahri-pagination]');
            this.pageNumbers = this.querySelector('[data-ahri-page-numbers]');
            this.pageStatus = this.querySelector('[data-ahri-page-status]');
            this.prevBtn = this.querySelector('[data-ahri-page-prev]');
            this.nextBtn = this.querySelector('[data-ahri-page-next]');
            this.activeFilter = 'all';
            this.activeCoilKey = '';

            this.hydrateSummaries();
            this.hideEmptyHeatKits();
            this.decorateHeatKits();
            this.syncAddSystemButtons();
            this.syncFilterAvailability();
            this.buildCoilChips();
            this.bind();
            this.apply();
            this.classList.add('is-ready');
          } catch (error) {
            this.classList.add('is-js-miss');
            const cards = this.items || Array.from(this.querySelectorAll('[data-ahri-item]'));
            cards.forEach((item) => {
              item.hidden = false;
              const wrap = item.parentElement;
              if (wrap && wrap.getAttribute('role') === 'listitem') wrap.hidden = false;
            });
            throw error;
          }
        }

        strings() {
          return window.ahriPackageStrings || {};
        }

        normalizeRow(row) {
          if (!row) return row;
          return this.withStocked(this.mapRawRow(row));
        }

        withStocked(row) {
          if (!row) return row;
          row.stocked = AhriListUtils.rowIsStocked(row);
          return row;
        }

        mapRawRow(row) {
          const outdoor = (row.outdoor && row.outdoor.value) || row.outdoor || {};
          const indoor = (row.indoor && row.indoor.value) || row.indoor || row.coil || {};
          const furnace = (row.furnace && row.furnace.value) || row.furnace || {};
          const indoorType = String(row.indoor_type || '').toLowerCase();
          const ahri = String(row.ahri_number || row.ahri || '').trim();
          const seer = String(row.seer2 || row.seer || row.SEER2 || '').trim();
          const partModel = (part, fallback) =>
            typeof part === 'string' ? part : part.model || part.title || part.sku || fallback || '';
          const outdoorModel = partModel(outdoor, row.outdoor_model);
          const indoorModel = partModel(indoor, row.indoor_model);
          const furnaceModel = partModel(furnace, row.furnace_model);
          const outdoorSku = AhriListUtils.partSku(outdoor, row.outdoor_sku);
          const indoorSku = AhriListUtils.partSku(indoor, row.indoor_sku);
          const furnaceSku = AhriListUtils.partSku(furnace, row.furnace_sku);
          const catalog = String(
            (typeof indoor === 'object' && indoor.in_catalog) || row.in_catalog || ''
          ).toLowerCase();
          const mapped = {
            ahri,
            seer,
            outdoor: outdoorModel,
            indoor: indoorModel,
            furnace: furnaceModel,
            indoor_type: indoorType,
            in_catalog: catalog,
            outdoor_catalog: String(
              (typeof outdoor === 'object' && outdoor && outdoor.in_catalog) || row.outdoor_catalog || ''
            ).toLowerCase(),
            summary: [indoorModel, furnaceModel].filter(Boolean).join(' + '),
            search: [ahri, seer, outdoorModel, indoorModel, furnaceModel, outdoorSku, indoorSku, furnaceSku]
              .filter(Boolean)
              .join(' '),
            roles: [],
            outdoor_sku: outdoorSku,
            indoor_sku: indoorSku,
            furnace_sku: furnaceSku,
            outdoor_handle:
              typeof outdoor === 'string' ? row.outdoor_handle || '' : outdoor.handle || row.outdoor_handle || '',
            indoor_handle:
              typeof indoor === 'string' ? row.indoor_handle || '' : indoor.handle || row.indoor_handle || '',
            furnace_handle:
              typeof furnace === 'string' ? row.furnace_handle || '' : furnace.handle || row.furnace_handle || '',
          };
          if (outdoorModel || outdoorSku) mapped.roles.push('outdoor');
          if (indoorType === 'air_handler' || /CB[AXK]/i.test(indoorModel)) mapped.roles.push('air_handler');
          else if (indoorModel || indoorSku) mapped.roles.push('coil');
          if (furnaceModel || furnaceSku) mapped.roles.push('furnace');
          return mapped;
        }

        parseListPayload(text) {
          const trimmed = String(text || '').trim();
          if (!trimmed) return null;
          const asPayload = (data) => {
            if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
            if (!Array.isArray(data.rows)) return null;
            if (data.rows.length > 5) return null;
            return {
              rows: data.rows,
              total: Math.max(0, Number(data.total) || 0),
              page: Math.max(1, Number(data.page) || 1),
              page_size: Math.max(1, Math.min(5, Number(data.page_size) || 5)),
            };
          };
          try {
            return asPayload(JSON.parse(trimmed));
          } catch (error) {
            const start = trimmed.indexOf('{');
            const end = trimmed.lastIndexOf('}');
            if (start < 0 || end <= start) return null;
            try {
              return asPayload(JSON.parse(trimmed.slice(start, end + 1)));
            } catch (inner) {
              return null;
            }
          }
        }

        listRequestUrl(page = this.page) {
          const root =
            (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
            '/';
          const base =
            this.listUrl && String(this.listUrl).indexOf('search') >= 0
              ? this.listUrl
              : `${root}search?view=ahri-list`;
          const filter = this.activeFilter && this.activeFilter !== 'all' ? this.activeFilter : '';
          const sort = this.seerSort && this.seerSort !== 'default' ? this.seerSort : '';
          const token = AhriListUtils.listRequestToken(
            (this.dataset && this.dataset.pdpHandle) || this.pdpHandle || '',
            page,
            this.searchQuery,
            filter,
            this.minSeer,
            sort,
            this.activeCoilKey,
            this.pageSize,
            this.pdpRole
          );
          const join = String(base).includes('?') ? '&' : '?';
          return `${base}${join}q=${encodeURIComponent(token)}`;
        }

        needsAllRows() {
          return Boolean(
            this.searchQuery ||
              this.minSeer ||
              this.activeFilter !== 'all' ||
              this.activeCoilKey ||
              this.seerSort !== 'default' ||
              this.page > 1
          );
        }

        listFetchTimeoutMs() {
          return Math.max(1, Number(this.dataset.ahriListTimeoutMs) || 8000);
        }

        async fetchList(url) {
          const timeoutMs = this.listFetchTimeoutMs();
          const controller = typeof AbortController === 'function' ? new AbortController() : null;
          const timer = setTimeout(() => {
            if (controller) controller.abort();
          }, timeoutMs);
          try {
            return await fetch(url, {
              headers: { Accept: 'text/html' },
              signal: controller ? controller.signal : undefined,
            });
          } catch (error) {
            if (error && error.name === 'AbortError') {
              const timeoutError = new Error('timeout');
              timeoutError.name = 'AbortError';
              throw timeoutError;
            }
            throw error;
          } finally {
            clearTimeout(timer);
          }
        }

        showListError() {
          if (this.listError) this.listError.hidden = false;
        }

        hideListError() {
          if (this.listError) this.listError.hidden = true;
        }

        // The stale "of N" must not sit next to a pressed chip while the
        // new page loads, so the status clears until applyVisible repaints.
        setListLoading(on) {
          if (this.listHost) {
            if (on) this.listHost.setAttribute('aria-busy', 'true');
            else this.listHost.removeAttribute('aria-busy');
          }
          if (this.listLoading) this.listLoading.hidden = !on;
          if (on && this.pageStatus) this.pageStatus.textContent = '';
        }

        // The last good server page survives a miss only when the failed
        // request asked for another page of the same filters and query.
        lastGoodDiffersByPageOnly() {
          return Boolean(
            this.loadedRequestKey &&
              this.page !== this.serverPage &&
              this.listRequestUrl(this.serverPage) === this.loadedRequestKey
          );
        }

        retryList() {
          this.listLoadFailed = false;
          this.failedRequestKey = '';
          this.rowsLoad = null;
          this.pendingRequestKey = '';
          this.hideListError();
          const token = (this.applyToken = (this.applyToken || 0) + 1);
          return this.ensureAllRows().then(() => {
            if (token !== this.applyToken) return;
            this.applyVisible();
          });
        }

        requestKeyChanged(key) {
          return Boolean(this.failedRequestKey && this.failedRequestKey !== key);
        }

        async ensureAllRows() {
          const key = this.listRequestUrl();
          if (this.listLoadFailed) {
            if (!this.requestKeyChanged(key)) return;
            this.listLoadFailed = false;
            this.failedRequestKey = '';
          }
          if (this.rowsLoaded && this.loadedRequestKey === key) return;
          if (this.rowsLoad && this.pendingRequestKey === key) return this.rowsLoad;
          this.pendingRequestKey = key;
          this.rowsLoaded = false;
          const page = this.page;
          const keepLastGood = this.lastGoodDiffersByPageOnly();
          this.rowsLoad = (async () => {
            if (!this.listUrl) {
              this.rowsLoaded = true;
              this.loadedRequestKey = key;
              return;
            }
            this.setListLoading(true);
            try {
              const res = await this.fetchList(key);
              if (!res.ok) {
                console.error({
                  source: 'ahri-compatible-systems',
                  event: 'ahri-list-fetch',
                  status: Number(res.status) || 0,
                  reason: 'http',
                });
                throw new Error('list miss');
              }
              const payload = this.parseListPayload(await res.text());
              if (!payload) {
                console.error({
                  source: 'ahri-compatible-systems',
                  event: 'ahri-list-fetch',
                  status: Number(res.status) || 0,
                  reason: 'not-json',
                });
                throw new Error('list parse');
              }
              if (this.pendingRequestKey !== key) return;
              this.serverRows = payload.rows.map((row) => this.normalizeRow(row)).filter((row) => row && row.ahri);
              if (!this.rowsByAhri) this.rowsByAhri = new Map();
              this.serverRows.forEach((row) => this.rowsByAhri.set(row.ahri, row));
              this.rowsLoaded = true;
              this.pagedFromServer = true;
              this.loadedRequestKey = key;
              this.failedRequestKey = '';
              this.listLoadFailed = false;
              this.serverPage = page;
              this.serverTotal = Math.max(0, Number(payload.total) || 0);
              this.listTotal = this.serverTotal;
              this.hideListError();
              this.syncFilterAvailability();
              this.buildCoilChips();
            } catch (error) {
              if (this.pendingRequestKey !== key) return;
              if (error && error.name === 'AbortError') {
                console.error({
                  source: 'ahri-compatible-systems',
                  event: 'ahri-list-fetch',
                  status: 0,
                  reason: 'timeout',
                });
              } else if (error.message !== 'list miss' && error.message !== 'list parse') {
                console.error({
                  source: 'ahri-compatible-systems',
                  event: 'ahri-list-fetch',
                  status: 0,
                  reason: 'network',
                });
              }
              this.rowsLoad = null;
              this.pendingRequestKey = '';
              this.pagedFromServer = keepLastGood;
              this.listLoadFailed = true;
              this.failedRequestKey = key;
              this.listTotal = this.items.length;
              this.showListError();
              this.syncFilterAvailability();
              this.buildCoilChips();
            } finally {
              if (!this.pendingRequestKey || this.pendingRequestKey === key) this.setListLoading(false);
            }
          })();
          return this.rowsLoad;
        }

        rowRoles(row) {
          return (row.roles || []).filter(Boolean);
        }

        rowHas(row, role) {
          if (!role) return true;
          return this.rowRoles(row).includes(role);
        }

        bindItem(item) {
          if (!item || item.dataset.ahriBound === 'true') return;
          item.dataset.ahriBound = 'true';
          const viewBtn = item.querySelector('[data-ahri-view-system]');
          if (viewBtn) {
            viewBtn.addEventListener('click', () => this.openDetail(item));
          }
          const backBtn = item.querySelector('[data-ahri-back-list]');
          if (backBtn) {
            backBtn.addEventListener('click', async () => {
              const returnTo = this.detailReturnFocus || item.querySelector('[data-ahri-view-system]');
              this.closeDetail();
              await this.apply();
              this.restoreDetailFocus(returnTo);
            });
          }
        }

        partnerEl(role, title, unmatched, letterOk) {
          const labels = this.strings();
          const article = document.createElement('article');
          article.className = 'ahri-partner';
          article.dataset.partnerRole = role;
          if (role === 'coil') {
            article.dataset.coilLetterOk = letterOk === false || unmatched ? 'false' : 'true';
          }
          const copy = document.createElement('div');
          copy.className = 'ahri-partner__copy';
          const roleEl = document.createElement('p');
          roleEl.className = 'ahri-partner__role';
          roleEl.textContent = labels[role] || role;
          const titleEl = document.createElement('p');
          titleEl.className = 'ahri-partner__title';
          titleEl.textContent = title || '';
          copy.appendChild(roleEl);
          copy.appendChild(titleEl);
          if (unmatched) {
            const note = document.createElement('p');
            note.className = 'ahri-partner__unmatched';
            note.appendChild(
              document.createTextNode(`${labels.no_stocked_coil || 'No stocked coil for this match.'} `)
            );
            const href = String(labels.phoneTel || labels.phone_tel || '').trim();
            const callText = labels.no_stocked_coil_call || 'Call us.';
            if (/^tel:\+\d{10,15}$/.test(href)) {
              const call = document.createElement('a');
              call.className = 'ahri-partner__call';
              call.href = href;
              call.textContent = callText;
              note.appendChild(call);
            } else {
              const call = document.createElement('span');
              call.textContent = callText;
              note.appendChild(call);
            }
            copy.appendChild(note);
          }
          article.appendChild(copy);
          return article;
        }

        mountRestRow(row) {
          const labels = this.strings();
          const wrap = document.createElement('div');
          wrap.className = 'ahri-compatible-systems__item ahri-page-rest';
          wrap.setAttribute('role', 'listitem');
          const item = document.createElement('article');
          item.className = 'ahri-combo';
          item.dataset.ahriItem = '';
          item.dataset.ahriNumber = row.ahri || '';
          item.dataset.seer = row.seer || '';
          item.dataset.outdoorModel = row.outdoor || '';
          item.dataset.search = row.search || '';
          const stocked = row.stocked !== false;
          item.dataset.ahriStocked = stocked ? 'true' : 'false';
          const summary = document.createElement('div');
          summary.className = 'ahri-combo__summary';
          const summaryCopy = document.createElement('div');
          summaryCopy.className = 'ahri-combo__summary-copy';
          if (row.summary) {
            const model = document.createElement('p');
            model.className = 'ahri-combo__summary-model';
            model.dataset.ahriSummaryModel = '';
            model.textContent = row.summary;
            summaryCopy.appendChild(model);
          }
          const meta = document.createElement('p');
          meta.className = 'ahri-combo__summary-meta';
          if (row.seer) {
            const seer = document.createElement('span');
            seer.className = 'ahri-combo__summary-seer';
            seer.textContent = `${labels.seer2 || 'SEER2'} ${row.seer}`;
            meta.appendChild(seer);
          }
          summaryCopy.appendChild(meta);
          const view = document.createElement('button');
          view.type = 'button';
          view.className = 'button button--secondary ahri-combo__view';
          view.dataset.ahriViewSystem = '';
          const viewLabel = document.createElement('span');
          viewLabel.textContent = labels.view_system || 'View System';
          view.appendChild(viewLabel);
          summary.appendChild(summaryCopy);
          summary.appendChild(view);
          const detail = document.createElement('div');
          detail.className = 'ahri-combo__detail';
          const heading = document.createElement('h3');
          heading.className = 'ahri-combo__title';
          const ahriLabel = document.createElement('span');
          ahriLabel.className = 'ahri-combo__label';
          ahriLabel.textContent = labels.ahri || 'AHRI #';
          const ahriNum = document.createElement('span');
          ahriNum.className = 'ahri-combo__number';
          ahriNum.textContent = row.ahri || '';
          heading.tabIndex = -1;
          heading.appendChild(ahriLabel);
          heading.appendChild(ahriNum);
          const partners = document.createElement('div');
          partners.className = 'ahri-combo__partners';
          const outdoorUnmatched =
            Boolean(row.outdoor) &&
            !AhriListUtils.partIsStocked(row.outdoor, row.outdoor_sku, row.outdoor_catalog);
          const indoorUnmatched = !stocked && !outdoorUnmatched;
          if (row.outdoor) partners.appendChild(this.partnerEl('outdoor', row.outdoor, outdoorUnmatched));
          if (row.indoor_type === 'air_handler' && row.indoor) {
            partners.appendChild(this.partnerEl('air_handler', row.indoor, indoorUnmatched));
          } else if (row.indoor) {
            partners.appendChild(this.partnerEl('coil', row.indoor, indoorUnmatched, stocked));
          }
          if (row.furnace) partners.appendChild(this.partnerEl('furnace', row.furnace, false));
          const footer = document.createElement('footer');
          footer.className = 'ahri-combo__system';
          const add = document.createElement('button');
          add.type = 'button';
          add.className = 'button button--primary ahri-add-system';
          add.dataset.ahriAddSystem = '';
          add.disabled = true;
          if (!stocked) add.hidden = true;
          const addLabel = document.createElement('span');
          addLabel.textContent = labels.add_system || 'Add system';
          add.appendChild(addLabel);
          const status = document.createElement('p');
          status.className = 'ahri-add-system__status';
          status.dataset.ahriSystemStatus = '';
          status.hidden = true;
          footer.appendChild(add);
          footer.appendChild(status);
          if (!stocked) footer.hidden = true;
          const back = document.createElement('button');
          back.type = 'button';
          back.className = 'button button--secondary ahri-combo__back';
          back.dataset.ahriBackList = '';
          const backLabel = document.createElement('span');
          backLabel.textContent = labels.back_to_list || 'Back to systems';
          back.appendChild(backLabel);
          detail.appendChild(heading);
          detail.appendChild(partners);
          detail.appendChild(back);
          item.appendChild(summary);
          item.appendChild(footer);
          item.appendChild(detail);
          wrap.appendChild(item);
          if (this.listHost) this.listHost.appendChild(wrap);
          this.bindItem(item);
          this.restByAhri.set(row.ahri, item);
          this.items = Array.from(this.querySelectorAll('[data-ahri-item]'));
          return item;
        }

        ensureRowItem(row) {
          const ahri = row && row.ahri;
          if (!ahri) return null;
          return this.inlineByAhri.get(ahri) || this.restByAhri.get(ahri) || this.mountRestRow(row);
        }

        bind() {
          this.filterButtons.forEach((button) => {
            button.addEventListener('click', () => this.setFilter(button.dataset.ahriFilter));
          });
          if (this.searchInput) {
            this.searchInput.addEventListener('input', () => {
              this.searchQuery = this.searchInput.value || '';
              this.page = 1;
              this.closeDetail();
              this.apply();
            });
          }
          if (this.seerMinSelect) {
            this.seerMinSelect.addEventListener('change', () => {
              this.minSeer = this.seerMinSelect.value || '';
              this.page = 1;
              this.closeDetail();
              this.apply();
            });
          }
          if (this.seerSortSelect) {
            this.seerSortSelect.addEventListener('change', () => {
              this.seerSort = this.seerSortSelect.value || 'default';
              this.page = 1;
              this.closeDetail();
              this.apply();
            });
          }
          if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.goToPage(this.shownPage - 1));
          }
          if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.goToPage(this.shownPage + 1));
          }
          this.items.forEach((item) => this.bindItem(item));
          if (this.listRetry) {
            this.listRetry.addEventListener('click', () => this.retryList());
          }
        }

        // The summary model is rendered by Liquid from matchup data by PDP role
        // (the other unit). Partner titles are Shopify titles and on outdoor
        // PDPs the outdoor row is this product, so never copy them here.
        hydrateSummaries() {
          this.items.forEach((item) => {
            const cents = Array.from(item.querySelectorAll('[data-price-cents]')).reduce((sum, el) => {
              const n = Number(el.dataset.priceCents || 0);
              return Number.isFinite(n) ? sum + n : sum;
            }, 0);
            const priceEl = item.querySelector('[data-ahri-summary-price]');
            const formatted = AhriListUtils.formatCents(cents);
            if (priceEl && formatted) {
              priceEl.hidden = false;
              priceEl.textContent = formatted;
              item.dataset.systemPrice = String(cents);
            }
          });
        }

        hideEmptyHeatKits() {
          if (this.heatKits && !this.heatKits.querySelector('[data-partner-role="heat_kit"]')) {
            this.heatKits.hidden = true;
          }
        }

        syncAddSystemButtons() {
          this.items.forEach((item) => {
            const add = item.querySelector('[data-ahri-add-system]');
            if (!add) return;
            const reason = item.querySelector('[data-ahri-add-reason]');
            if (item.dataset.ahriStocked === 'false' || item.querySelector('[data-coil-letter-ok="false"]')) {
              add.hidden = true;
              add.disabled = true;
              if (reason) reason.hidden = true;
              return;
            }
            const partners = Array.from(item.querySelectorAll('[data-partner-role]')).filter((el) => !el.hidden);
            const hasIds =
              partners.length > 0 && partners.every((el) => el.hasAttribute('data-variant-id'));
            add.hidden = false;
            add.disabled = !hasIds;
            if (reason) {
              reason.hidden = hasIds;
              if (!hasIds && reason.id) add.setAttribute('aria-describedby', reason.id);
              else add.removeAttribute('aria-describedby');
            }
          });
        }

        // List JS loads before cart JS. First paint uses AhriListUtils; cart JS
        // re-calls this after AhriPackageUtils exists so the two stay in sync.
        decorateHeatKits() {
          if (!this.heatKits) return;
          const list = this.heatKits.querySelector('.ahri-heat-kits__list');
          const cards = Array.from(this.heatKits.querySelectorAll('[data-partner-role="heat_kit"]'));
          if (!list || !cards.length) return;
          const airHandler = {
            title: this.dataset.pdpTitle || '',
            handle: this.dataset.pdpHandle || '',
            type: this.dataset.pdpType || '',
          };
          const systemBtu = AhriListUtils.pdpSystemBtu(this.dataset);
          const label = this.heatKits.dataset.recommendedLabel || 'Recommended';
          const ranked = AhriListUtils.sortHeatKitCards(
            cards.map((card) => ({ card, kit: AhriListUtils.kitFromHeatKitCard(card) })),
            systemBtu,
            airHandler
          );
          ranked.forEach((row) => {
            const recommended = AhriListUtils.heatKitIsRecommended(row.kit, systemBtu);
            row.card.classList.toggle('is-recommended', recommended);
            let badge = row.card.querySelector('[data-ahri-kit-recommended]');
            if (recommended) {
              if (!badge) {
                badge = document.createElement('span');
                badge.className = 'ahri-partner__recommend';
                badge.setAttribute('data-ahri-kit-recommended', '');
                const role = row.card.querySelector('.ahri-partner__role');
                const title = row.card.querySelector('.ahri-partner__title');
                if (role) role.appendChild(badge);
                else if (title) title.prepend(badge);
              }
              badge.textContent = label;
            } else if (badge) {
              badge.remove();
            }
            list.appendChild(row.card);
          });
        }

        rolesInItems() {
          const roles = new Set();
          this.items.forEach((item) => {
            item.querySelectorAll('[data-partner-role]').forEach((el) => {
              if (el.dataset.partnerRole) roles.add(el.dataset.partnerRole);
            });
          });
          (this.serverRows || []).forEach((row) => {
            this.rowRoles(row).forEach((role) => roles.add(role));
          });
          if (this.heatKits && !this.heatKits.hidden) roles.add('heat_kit');
          return roles;
        }

        syncFilterAvailability() {
          const roles = this.rolesInItems();
          this.filterButtons.forEach((button) => {
            const key = button.dataset.ahriFilter;
            if (key === 'all') {
              button.hidden = false;
              return;
            }
            button.hidden = !roles.has(key);
          });
        }

        uniqueCoils() {
          const coils = [];
          const seen = new Set();
          this.items.forEach((item) => {
            item.querySelectorAll('[data-partner-role="coil"]').forEach((el) => {
              const key = (el.dataset.partnerKey || '').trim();
              const title = el.querySelector('.ahri-partner__title');
              const label = title ? title.textContent.trim() : key;
              if (key && !seen.has(key)) {
                seen.add(key);
                coils.push({ key, label });
              }
            });
          });
          (this.serverRows || []).forEach((row) => {
            if (!this.rowHas(row, 'coil') || !row.indoor) return;
            const key = (row.indoor_handle || row.indoor_sku || row.indoor || '').trim();
            if (key && !seen.has(key)) {
              seen.add(key);
              coils.push({ key, label: row.indoor });
            }
          });
          return coils;
        }

        buildCoilChips() {
          if (!this.coilChipHost) return;
          this.coilChipHost.innerHTML = '';
          const coils = this.uniqueCoils();
          if (coils.length < 2) return;

          coils.forEach((coil) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'ahri-filter';
            button.dataset.ahriCoilKey = coil.key;
            const label = document.createElement('span');
          label.textContent = coil.label;
          button.appendChild(label);
            button.setAttribute('aria-pressed', 'false');
            button.addEventListener('click', () => this.setCoil(coil.key));
            this.coilChipHost.appendChild(button);
          });
        }

        setFilter(filter) {
          this.activeFilter = filter || 'all';
          this.filterButtons.forEach((button) => {
            const on = button.dataset.ahriFilter === this.activeFilter;
            button.classList.toggle('is-active', on);
            button.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          if (this.activeFilter !== 'coil') this.activeCoilKey = '';
          this.page = 1;
          this.closeDetail();
          this.apply();
        }

        setCoil(key) {
          this.activeCoilKey = this.activeCoilKey === key ? '' : key;
          if (this.coilChipHost) {
            this.coilChipHost.querySelectorAll('[data-ahri-coil-key]').forEach((button) => {
              const on = button.dataset.ahriCoilKey === this.activeCoilKey;
              button.classList.toggle('is-active', on);
              button.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
          }
          this.page = 1;
          this.closeDetail();
          this.apply();
        }

        pairWidthCompatible(item) {
          const coil = item.querySelector('[data-partner-role="coil"]');
          const furnace = item.querySelector('[data-partner-role="furnace"]');
          if (!coil || !furnace) return true;
          return AhriListUtils.widthsEqual(coil.dataset.width, furnace.dataset.width);
        }

        matchesPdpWidth(el) {
          if (!el) return false;
          return AhriListUtils.widthsEqual(this.pdpWidth, el.dataset.width);
        }

        itemHas(item, role) {
          return Boolean(item.querySelector(`[data-partner-role="${role}"]`));
        }

        // The one list filter rule. It must match snippets/ahri-list-page.liquid,
        // which counts the server total, so change both together. Width rules
        // only hide a partner (applyPartnerVisibility); they never drop a row.
        shouldShowRow(row) {
          const filter = this.activeFilter;
          const hasOutdoor = this.rowHas(row, 'outdoor');
          const hasFurnace = this.rowHas(row, 'furnace');
          const hasCoil = this.rowHas(row, 'coil');
          const hasAirHandler = this.rowHas(row, 'air_handler');
          const searchHay =
            row.search ||
            [row.ahri, row.seer, row.outdoor, row.indoor, row.furnace, row.summary].filter(Boolean).join(' ');

          if (this.activeCoilKey) {
            const key = (row.indoor_handle || row.indoor_sku || row.indoor || '').trim();
            if (key !== this.activeCoilKey) return false;
          }

          if (!AhriListUtils.matchesSearch(searchHay, this.searchQuery)) return false;

          if (this.minSeer) {
            const seer = AhriListUtils.parseSeer(row.seer);
            if (seer == null || seer < Number(this.minSeer)) return false;
          }

          if (this.pdpRole === 'outdoor') {
            if (filter === 'furnace') return hasFurnace;
            if (filter === 'coil') return hasCoil;
            if (filter === 'air_handler') return hasAirHandler;
            return true;
          }
          if (this.pdpRole === 'furnace') {
            if (filter === 'outdoor') return hasOutdoor;
            if (filter === 'coil') return hasCoil;
            return true;
          }
          if (this.pdpRole === 'air_handler') {
            if (filter === 'outdoor') return hasOutdoor;
            if (filter === 'heat_kit') return false;
            return true;
          }
          if (this.pdpRole === 'coil') {
            if (filter === 'outdoor') return hasOutdoor;
            if (filter === 'furnace') return hasFurnace;
            return true;
          }
          if (filter === 'all') return true;
          return this.rowHas(row, filter);
        }

        // Inline cards are judged from their own markup, never from a server
        // row that happens to share the AHRI, so the miss fallback count is
        // the same whichever pages loaded before it.
        itemRow(item) {
          const coil = item.querySelector('[data-partner-role="coil"]');
          return {
            ahri: item.dataset.ahriNumber,
            seer: item.dataset.seer,
            search: AhriListUtils.searchHay(item),
            roles: ['outdoor', 'coil', 'air_handler', 'furnace'].filter((role) => this.itemHas(item, role)),
            indoor_handle: coil ? coil.dataset.partnerKey || '' : '',
          };
        }

        shouldShowItem(item) {
          return this.shouldShowRow(this.itemRow(item));
        }

        applyPartnerVisibility(item, visible) {
          if (!visible) return;
          const hasCoil = this.itemHas(item, 'coil');
          const widthOk = this.pairWidthCompatible(item);
          const furnace = item.querySelector('[data-partner-role="furnace"]');
          const coil = item.querySelector('[data-partner-role="coil"]');

          if (furnace) {
            let showFurnace = true;
            if (this.pdpRole === 'outdoor' && hasCoil && !widthOk) showFurnace = false;
            if (this.pdpRole === 'coil' && !this.matchesPdpWidth(furnace)) showFurnace = false;
            if (this.activeFilter === 'air_handler' || this.activeFilter === 'heat_kit') showFurnace = false;
            furnace.hidden = !showFurnace;
          }

          if (coil) {
            let showCoil = true;
            if (this.pdpRole === 'furnace' && !this.matchesPdpWidth(coil)) showCoil = false;
            if (this.activeFilter === 'air_handler' || this.activeFilter === 'outdoor' || this.activeFilter === 'heat_kit') {
              if (this.pdpRole === 'furnace' && this.activeFilter === 'outdoor') showCoil = false;
              if (this.pdpRole === 'air_handler') showCoil = false;
              if (this.pdpRole === 'outdoor' && this.activeFilter === 'air_handler') showCoil = false;
            }
            coil.hidden = !showCoil;
          }
        }

        async resolvePartFromHandle(part) {
          const handle = String((part && part.handle) || '').trim();
          if (!handle) return part;
          try {
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
              headers: { Accept: 'application/json' },
            });
            if (!res.ok) return part;
            const product = await res.json();
            const sku = String((part && part.sku) || '').trim();
            const variants = product.variants || [];
            const variant =
              (sku && variants.find((entry) => String((entry && entry.sku) || '').trim() === sku)) ||
              variants.find((entry) => entry.available) ||
              variants[0];
            if (!variant) return part;
            return Object.assign({}, part, {
              id: variant.id,
              sku: variant.sku || sku,
              available: Boolean(variant.available),
            });
          } catch (error) {
            return part;
          }
        }

        async hydrateRestCart(item) {
          if (!item || item.querySelector('[data-variant-id]')) return;
          const row = (this.rowsByAhri || new Map()).get(item.dataset.ahriNumber);
          if (!row) return;
          const parts = [];
          if (row.outdoor || row.outdoor_handle) {
            parts.push({
              role: 'outdoor',
              handle: row.outdoor_handle,
              sku: row.outdoor_sku,
              title: row.outdoor,
            });
          }
          if (row.indoor || row.indoor_handle) {
            parts.push({
              role: row.indoor_type === 'air_handler' ? 'air_handler' : 'coil',
              handle: row.indoor_handle,
              sku: row.indoor_sku,
              title: row.indoor,
              letter_ok: row.stocked !== false,
            });
          }
          if (row.furnace || row.furnace_handle) {
            parts.push({
              role: 'furnace',
              handle: row.furnace_handle,
              sku: row.furnace_sku,
              title: row.furnace,
            });
          }
          let resolved = parts;
          if (window.AhriPackageUtils && typeof window.AhriPackageUtils.hydratePartnerList === 'function') {
            resolved = await window.AhriPackageUtils.hydratePartnerList(parts);
          } else {
            resolved = await Promise.all(parts.map((part) => this.resolvePartFromHandle(part)));
          }
          resolved.forEach((part) => {
            if (!part || !part.id) return;
            const el = item.querySelector(`[data-partner-role="${part.role}"]`);
            if (el) el.dataset.variantId = String(part.id);
          });
          this.syncAddSystemButtons();
        }

        stickyChromeClearance() {
          const root = document.documentElement;
          if (!root || typeof window.getComputedStyle !== 'function') return 0;
          const style = window.getComputedStyle(root);
          const chrome = parseFloat(style.getPropertyValue('--sticky-chrome-height')) || 0;
          const rem = parseFloat(style.fontSize) || 0;
          return chrome + rem * 0.8;
        }

        detailTargetNeedsScroll(el) {
          if (!el || typeof el.getBoundingClientRect !== 'function') return false;
          return AhriListUtils.detailFocusNeedsScroll(
            el.getBoundingClientRect(),
            this.stickyChromeClearance(),
            window.innerWidth || 0,
            window.innerHeight || 0
          );
        }

        scrollDetailTarget(el, behavior, block) {
          if (!el || typeof el.scrollIntoView !== 'function') return;
          el.scrollIntoView({ behavior, block });
          if (window.PageScrollChrome && typeof window.PageScrollChrome.schedule === 'function') {
            window.PageScrollChrome.schedule();
          }
          // Smooth open must keep one animation. Auto (reduced motion) may
          // correct after #147 writes --sticky-chrome-height. Do not call
          // syncStickyChromeHeight; that writer stays on #147's scroll,
          // resize, and observer path.
          if (behavior === 'smooth') return;
          if (typeof requestAnimationFrame !== 'function') return;
          const started = Date.now();
          const maxFrames = 120;
          let frames = 0;
          const follow = () => {
            frames += 1;
            if (!el.isConnected) return;
            if (this.detailTargetNeedsScroll(el)) {
              el.scrollIntoView({ behavior: 'auto', block });
              const clearance = this.stickyChromeClearance();
              const top = el.getBoundingClientRect().top;
              if (top + 1 < clearance) window.scrollBy(0, top - clearance);
            }
            if (
              frames < maxFrames &&
              Date.now() - started < 2000 &&
              this.detailTargetNeedsScroll(el)
            ) {
              requestAnimationFrame(follow);
            }
          };
          requestAnimationFrame(follow);
        }

        focusDetailHeading(item) {
          const heading = item && item.querySelector('.ahri-combo__title');
          if (!heading) return;
          heading.tabIndex = -1;
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollDetailTarget(heading, reduce ? 'auto' : 'smooth', 'start');
          heading.focus({ preventScroll: true });
        }

        restoreDetailFocus(target) {
          if (!target || !target.isConnected || target.disabled || target.hidden) return;
          const style = window.getComputedStyle(target);
          if (style.display === 'none' || style.visibility === 'hidden') return;
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollDetailTarget(target, reduce ? 'auto' : 'smooth', 'nearest');
          target.focus({ preventScroll: true });
        }

        async openDetail(item) {
          this.detailReturnFocus = item.querySelector('[data-ahri-view-system]');
          this.viewingItem = item;
          await this.apply();
          this.focusDetailHeading(item);
          await this.hydrateRestCart(item);
        }

        closeDetail() {
          this.viewingItem = null;
          this.detailReturnFocus = null;
        }

        goToPage(page) {
          this.page = Math.max(1, Number(page) || 1);
          this.closeDetail();
          this.apply();
        }

        inlineItems() {
          if (this.inlineByAhri && this.inlineByAhri.size) {
            return Array.from(this.inlineByAhri.values());
          }
          return this.items || [];
        }

        matchedItems() {
          return this.inlineItems().filter((item) => this.shouldShowItem(item));
        }

        matchedRows() {
          return this.matchedItems().map((item) => ({
            ahri: item.dataset.ahriNumber,
            seer: item.dataset.seer,
            search: item.dataset.search,
          }));
        }

        apply() {
          if (this.needsAllRows()) {
            const token = (this.applyToken = (this.applyToken || 0) + 1);
            return this.ensureAllRows().then(() => {
              if (token !== this.applyToken) return;
              this.applyVisible();
            });
          }
          this.setListLoading(false);
          this.applyVisible();
        }

        syncEmptyCopy() {
          if (!this.empty) return;
          const labels = this.strings();
          const text = this.searchQuery
            ? labels.search_empty || this.empty.textContent
            : labels.filter_empty || this.empty.textContent;
          if (text) this.empty.textContent = text;
        }

        applyVisible() {
          if (this.coilFilters) {
            const showCoilRow = this.activeFilter === 'coil' && this.coilChipHost && this.coilChipHost.children.length > 0;
            this.coilFilters.hidden = !showCoilRow;
          }

          this.syncEmptyCopy();

          // A miss keeps pagedFromServer only when lastGoodDiffersByPageOnly.
          // Otherwise the inline cards stand in, and their count is partial
          // when the section rendered fewer cards than the catalog total.
          const usingServer = this.needsAllRows() && this.pagedFromServer;
          const partial =
            this.listLoadFailed && !usingServer && this.inlineItems().length < Number(this.catalogTotal || 0);
          this.shownPage = usingServer && this.listLoadFailed ? this.serverPage : this.page;
          let matched;
          let total;
          if (usingServer) {
            matched = this.serverRows || [];
            total = Math.max(0, Number(this.serverTotal) || 0);
          } else {
            matched = this.matchedRows();
            if (this.seerSort === 'asc' || this.seerSort === 'desc') {
              matched = matched.slice().sort((a, b) =>
                AhriListUtils.compareSeer(
                  a.seer || (a.dataset && a.dataset.seer),
                  b.seer || (b.dataset && b.dataset.seer),
                  this.seerSort
                )
              );
            }
            total = matched.length;
            if (!this.listLoadFailed && !this.needsAllRows()) {
              total = Math.max(total, Math.max(0, Number(this.listTotal) || 0));
            }
          }

          const pages = AhriListUtils.pageCount(total, this.pageSize);
          if (this.page > pages) this.page = pages;
          const pageRows = this.viewingItem
            ? [
                (this.rowsByAhri && this.rowsByAhri.get(this.viewingItem.dataset.ahriNumber)) ||
                  matched.find((row) => row.ahri === this.viewingItem.dataset.ahriNumber) || {
                    ahri: this.viewingItem.dataset.ahriNumber,
                  },
              ]
            : usingServer
              ? matched
              : AhriListUtils.pageSlice(matched, this.page, this.pageSize);
          const pageItems = pageRows.map((row) => this.ensureRowItem(row)).filter(Boolean);
          const onPage = new Set(pageItems);

          this.querySelectorAll('[data-ahri-item]').forEach((item) => {
            const show = onPage.has(item);
            item.classList.toggle('is-open', this.viewingItem === item);
            item.hidden = !show;
            if (item.parentElement && item.parentElement.hasAttribute('role')) {
              item.parentElement.hidden = !show;
            }
            this.applyPartnerVisibility(item, show);
          });
          this.syncAddSystemButtons();

          if (this.heatKits) {
            const showKits =
              !this.heatKits.querySelector('[data-partner-role="heat_kit"]')
                ? false
                : this.pdpRole === 'air_handler' &&
                  !this.viewingItem &&
                  (this.activeFilter === 'all' || this.activeFilter === 'heat_kit');
            this.heatKits.hidden = !showKits;
          }

          if (this.empty) {
            const kitsVisible = this.heatKits && !this.heatKits.hidden;
            this.empty.hidden = total > 0 || Boolean(kitsVisible);
          }

          this.renderPagination(total, partial);
        }

        renderPagination(total, partial) {
          if (!this.pagination) return;
          if (this.pageStatus && total === 0) {
            this.pageStatus.textContent = '';
            this.pageStatus.hidden = true;
          } else if (this.pageStatus) {
            this.pageStatus.hidden = false;
          }
          const pages = AhriListUtils.pageCount(total, this.pageSize);
          const show = total > this.pageSize && !this.viewingItem;
          this.pagination.hidden = !show;
          if (!show) return;

          const current = this.shownPage;
          if (this.prevBtn) this.prevBtn.disabled = current <= 1;
          if (this.nextBtn) this.nextBtn.disabled = current >= pages;
          if (this.pageNumbers) {
            this.pageNumbers.innerHTML = '';
            AhriListUtils.visiblePageNumbers(current, pages).forEach((entry) => {
              if (entry === '…') {
                const ellipsis = document.createElement('span');
                ellipsis.className = 'ahri-pagination__ellipsis';
                ellipsis.textContent = '…';
                this.pageNumbers.appendChild(ellipsis);
                return;
              }
              const button = document.createElement('button');
              button.type = 'button';
              button.className = `ahri-pagination__num${entry === current ? ' is-active' : ''}`;
              button.textContent = String(entry);
              button.setAttribute('aria-current', entry === current ? 'page' : 'false');
              button.addEventListener('click', () => this.goToPage(entry));
              this.pageNumbers.appendChild(button);
            });
          }
          if (this.pageStatus) {
            const start = (current - 1) * this.pageSize + 1;
            const end = Math.min(total, current * this.pageSize);
            const labels = this.strings();
            const template =
              (partial && labels.pagination_status_partial) || labels.pagination_status || '[start]–[end] of [total]';
            this.pageStatus.textContent = String(template)
              .replace(/\[start\]/g, String(start))
              .replace(/\[end\]/g, String(end))
              .replace(/\[total\]/g, String(total));
          }
        }
      }
    );
  }
  } catch (error) {
    document.querySelectorAll('.ahri-compatible-systems:not(.is-ready)').forEach((el) => {
      el.classList.add('is-js-miss');
    });
    throw error;
  }
})();
